import React, { useState, useEffect } from 'react';
import { Activity, User, Shield, Terminal, Fingerprint, Clock, Search, Filter, Database, AlertTriangle, Globe } from 'lucide-react';
import adminService from '../../services/adminService';

const AuditLogs = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loginLogs, setLoginLogs] = useState([]);
    const [activeTab, setActiveTab] = useState('audit');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 30000); // 30s Real-time poll
        return () => clearInterval(interval);
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const [aLogs, lLogs] = await Promise.all([
                adminService.getAuditLogs(),
                adminService.getLoginLogs()
            ]);
            setAuditLogs(aLogs);
            setLoginLogs(lLogs);
        } catch (error) {
            console.error('Failed to fetch logs', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in h-full flex flex-col">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-widest">Activity Logs</h1>
                    <p className="text-slate-500 mt-2 font-medium">A detailed history of all system changes and login attempts.</p>
                </div>
                <div className="flex items-center text-[#4f46e5] bg-[#4f46e5]/10 px-4 py-2 rounded-2xl text-[10px] font-bold uppercase tracking-[0.1em] border border-[#4f46e5]/20 shadow-sm relative overflow-hidden group">
                    <div className="absolute inset-0 bg-[#4f46e5]/5 animate-pulse"></div>
                    <Shield size={14} className="mr-2 relative z-10" />
                    <span className="relative z-10">System Protection: Active</span>
                </div>
            </header>

            <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-2xl w-fit border border-[rgb(var(--border))]">
                <button
                    className={`flex items-center px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${activeTab === 'audit'
                        ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-sm'
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                        }`}
                    onClick={() => setActiveTab('audit')}
                >
                    <Terminal size={14} className="mr-2" />
                    Admin Actions
                </button>
                <button
                    className={`flex items-center px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${activeTab === 'login'
                        ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-sm'
                        : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                        }`}
                    onClick={() => setActiveTab('login')}
                >
                    <Fingerprint size={14} className="mr-2" />
                    Login History
                </button>
            </div>

            <div className="enterprise-card flex-1 overflow-hidden flex flex-col relative min-h-[500px]">
                <div className="p-6 border-b border-[rgb(var(--border))] glass flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                        <Database size={14} className="text-[#4f46e5]" />
                        <span>Format: Secure Archive</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="premium-input flex items-center !p-1 !px-3">
                            <Search size={14} className="text-slate-400 mr-2" />
                            <input
                                type="text"
                                placeholder="Filter logs..."
                                className="bg-transparent border-none p-0 focus:ring-0 text-[10px] uppercase font-bold tracking-widest w-40"
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-y-auto flex-1 custom-scrollbar">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-[rgb(var(--border))] sticky top-0 z-20">
                            {activeTab === 'audit' ? (
                                <tr>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Log ID & Type</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">User Email</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Action Details</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] text-right">Timestamp</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">User Email</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">IP Address</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Result</th>
                                    <th className="px-5 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] text-right">Time</th>
                                </tr>
                            )}
                        </thead>
                        <tbody className="divide-y divide-[rgb(var(--border))]">
                            {loading ? (
                                Array(10).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="4" className="px-5 py-4 h-16 bg-slate-50/20 dark:bg-slate-800/20"></td>
                                    </tr>
                                ))
                            ) : activeTab === 'audit' ? (
                                auditLogs.map((log) => (
                                    <tr key={log.id} className="group hover:bg-slate-50/50 dark:hover:bg-[#4f46e5]/5 transition-all">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 rounded-lg bg-[#4f46e5]/10 text-[#4f46e5] flex items-center justify-center">
                                                    <Activity size={16} />
                                                </div>
                                                <span className="text-xs font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider">{log.action}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{log.userEmail || 'AUTO-SYSTEM'}</span>
                                                <span className="text-[10px] text-slate-400 uppercase tracking-tighter">User ID: {Math.random().toString(36).substring(7)}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-xs text-slate-500 font-mono italic">
                                            <div className="max-w-md truncate bg-slate-50 dark:bg-slate-900/40 p-2 rounded-lg border border-[rgb(var(--border))]">
                                                {log.details}
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <div className="flex flex-col items-end">
                                                <span className="text-xs font-bold text-slate-900 dark:text-slate-100 tracking-tight">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{new Date(log.timestamp).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                loginLogs.map((log) => (
                                    <tr key={log.id} className="group hover:bg-slate-50/50 dark:hover:bg-[#4f46e5]/5 transition-all">
                                        <td className="px-5 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400">
                                                    <User size={18} />
                                                </div>
                                                <span className="text-sm font-extrabold text-slate-900 dark:text-slate-100 italic tracking-tight">{log.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-xs text-slate-500 font-mono font-bold">
                                            <div className="flex items-center space-x-2">
                                                <Globe size={12} className="text-[#4f46e5]" />
                                                <span className="text-slate-700 dark:text-slate-300">
                                                    {log.ipAddress}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className={`inline-flex items-center px-4 py-1.5 rounded-xl text-[10px] font-extrabold uppercase tracking-[0.15em] ${log.success
                                                ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                                : 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                                                }`}>
                                                {log.success ? (
                                                    <div className="flex items-center uppercase">
                                                        <Shield size={10} className="mr-1.5" />
                                                        Success
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center uppercase">
                                                        <AlertTriangle size={10} className="mr-1.5" />
                                                        Failed
                                                    </div>
                                                )}
                                            </span>
                                            {!log.success && (
                                                <p className="text-[8px] text-red-400 font-bold mt-1 uppercase tracking-tighter ml-1">
                                                    Reason: {log.failureReason}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-5 py-4 text-right">
                                            <div className="flex flex-col items-end">
                                                <span className="text-xs font-bold text-slate-900 dark:text-slate-100 tracking-tight">{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{new Date(log.timestamp).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AuditLogs;
